import { aiMessages } from '@atlaskit/editor-common/ai-messages/ai';
import { ACTION_SUBJECT_ID } from '@atlaskit/editor-common/analytics/types/enums';
import type { FloatingToolbarSeparator } from '@atlaskit/editor-common/types/floating-toolbar';
import type { ExtractInjectionAPI } from '@atlaskit/editor-common/types/next-editor-plugin';
import type { SelectionToolbarHandler } from '@atlaskit/editor-common/types/selection-toolbar';
import { fg } from '@atlaskit/platform-feature-flags/fg';
import { editorExperiment } from '@atlaskit/tmp-editor-statsig/editor-experiment';

import type { AIPlugin } from '../../aiPluginType';
import type { AIGlobalOptIn, EditorPluginAIProvider } from '../../types/types';
import { selectionCharWordCount, fireButtonViewedAnalyticsEvent } from '../utils/analytics/utils';
import {
	getAIExperienceButton,
	getImproveWritingAIButton,
	getAskAIButton,
	getAskRovoAiSplitButton,
} from './utils/floating-toolbar-button';
import { getIsAiEntryPointDisabled } from './utils/use-is-ai-entry-point-disabled';

export const getSelectionToolbarConfig =
	({
		editorPluginAIProvider,
		aiGlobalOptIn,
		api,
	}: {
		aiGlobalOptIn: AIGlobalOptIn;
		api: ExtractInjectionAPI<AIPlugin> | undefined;
		editorPluginAIProvider: EditorPluginAIProvider;
	}): SelectionToolbarHandler =>
	(state, intl) => {
		if (editorPluginAIProvider.disableAISelectionToolbar) {
			return;
		}
		const disabled = getIsAiEntryPointDisabled(api);

		const isAiButtonPulse =
			api?.engagementPlatform?.sharedState.currentState()?.messageStates[
				'cc-editor-ai_ai-toolbar-button-pulse'
			];

		const handleAiButtonMount = () => {
			if (editorPluginAIProvider.isInDocumentStreamingEnabled) {
				const { charCount, wordCount } = selectionCharWordCount(state);

				fireButtonViewedAnalyticsEvent({
					api,
					actionSubjectId: ACTION_SUBJECT_ID.AI_ASK_ROVO_BUTTON,
					attributes: {
						selectionCharCount: charCount,
						selectionWordCount: wordCount,
					},
				});
			}
			// Multiple calls to startMessage with the same message key will not result in multiple messages being shown.
			// That's why we can call it here without worrying about it being called multiple times.
			api?.engagementPlatform?.actions.startMessage('cc-editor-ai_ai-toolbar-button-pulse');
		};

		let handleImproveWritingButtonMount: (() => void) | undefined;

		if (editorPluginAIProvider.isInDocumentStreamingEnabled) {
			const { charCount, wordCount } = selectionCharWordCount(state);

			handleImproveWritingButtonMount = () => {
				fireButtonViewedAnalyticsEvent({
					api,
					actionSubjectId: ACTION_SUBJECT_ID.AI_HERO_PROMPT_BUTTON,
					attributes: {
						selectionCharCount: charCount,
						selectionWordCount: wordCount,
					},
				});
			};
		}

		const improveWritingBtn = getImproveWritingAIButton({
			intl,
			hideTitle: false,
			aiGlobalOptIn,
			disabled,
			onMount: handleImproveWritingButtonMount,
			isInDocumentStreamingEnabled: editorPluginAIProvider.isInDocumentStreamingEnabled,
		});

		if (editorPluginAIProvider.isInDocumentStreamingEnabled) {
			const aIButton = getAskRovoAiSplitButton({
				intl,
				rangeBaseGenerate: editorPluginAIProvider.baseGenerate,
				aiGlobalOptIn,
				disabled,
				editorPluginAIProvider,
				api,
				pulse: isAiButtonPulse,
				onMount: handleAiButtonMount,
			});

			return { rank: 10, isToolbarAbove: true, items: [aIButton, improveWritingBtn] };
		}

		if (fg('ai-smart-button-team-25') || editorExperiment('platform_editor_controls', 'variant1')) {
			const askAIButton = getAskAIButton({
				intl,
				rangeBaseGenerate: editorPluginAIProvider.baseGenerate,
				aiGlobalOptIn,
				pulse: isAiButtonPulse,
				onMount: handleAiButtonMount,
				disabled,
			});

			const separator: FloatingToolbarSeparator = { type: 'separator', fullHeight: true };

			/**
			 * We want to show AI button on left most side of floating toolbar.
			 * Higher the rank, the more left it will be.
			 * We are setting rank to 10 to show AI button on left most side.
			 */
			return {
				rank: 10,
				isToolbarAbove: true,
				items: [
					improveWritingBtn,
					askAIButton,
					...(editorExperiment('platform_editor_controls', 'variant1') ? [separator] : []),
				],
			};
		} else {
			const aiExperienceBtn = getAIExperienceButton({
				intl,
				rangeBaseGenerate: editorPluginAIProvider.baseGenerate,
				hideTitle: true,
				aiGlobalOptIn,
				title: aiMessages.tryAIToolbarIconTitle,
				tooltip: aiMessages.tryAIToolbarIconTooltip,
				pulse: isAiButtonPulse,
				onMount: handleAiButtonMount,
				disabled,
			});

			return { rank: -10, isToolbarAbove: true, items: [improveWritingBtn, aiExperienceBtn] };
		}
	};
